import {
    IRegistrationStartResponse,
    IAllRegistrationData,
    IRegistrationContactDetails,
    IRegistrationIdentity,
    IRegistrationIdentityResponse,
    IRegistrationPersonalDetails,
    IPhoneRequestData,
    IRegistrationProfessionalDetails,
    RegistrationSteps,
    IDynamicQuestion,
    IRegistrationAddress,
    IRegistrationTemplateRequestParams,
    IRegistrationTemplateResponse,
} from '@/types/types';
import { Config, makeFetcher } from '@/utils/fetch';
import { config } from '@/config';

const makeRegisterService = (apiUrl: string) => {
    const fetcher = makeFetcher(apiUrl);

    return {
        getTemplate: async (
            params: Partial<IRegistrationTemplateRequestParams>,
        ): Promise<IRegistrationTemplateResponse | undefined> => {
            const queryParams = Object.entries(params)
                .filter(([, value]) => value)
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join('&');
            if (!queryParams) return;
            const res = await fetcher.get(`/template?${queryParams}`);
            const data = (await res.json()) as Promise<IRegistrationTemplateResponse>;
            return data;
        },

        // Registration steps
        startRegistration: async (values: Partial<IRegistrationPersonalDetails>) => {
            const res = await fetcher.post('', JSON.stringify(values));
            const { registrationId } = (await res.json()) as IRegistrationStartResponse;
            return registrationId;
        },

        getRegistrationData: async (registrationId: string, config?: Config) => {
            const res = await fetcher.get(`/${registrationId}`, config);
            const data = (await res.json()) as Promise<IAllRegistrationData>;
            return data;
        },

        putPersonalDetails: async (
            registrationId: string,
            data: Partial<IRegistrationPersonalDetails>,
        ) => {
            const res = await fetcher.put(
                `/${registrationId}/${RegistrationSteps.Personal}`,
                JSON.stringify(data),
            );
            return res.status;
        },

        getProfessionalQuestions: async (professionId: string, config?: Config) => {
            const res = await fetcher.get(
                `/professional-details-questions/${professionId}`,
                config,
            );
            const data = res.json() as Promise<IDynamicQuestion[]>;
            return data;
        },

        putProfessionalDetails: async (
            registrationId: string,
            data: Partial<IRegistrationProfessionalDetails>,
        ) => {
            const res = await fetcher.put(
                `/${registrationId}/${RegistrationSteps.Professional}`,
                JSON.stringify(data),
            );
            return res.status;
        },

        putContactDetails: async (
            registrationId: string,
            formData: Partial<IRegistrationContactDetails>,
        ) => {
            const res = await fetcher.put(
                `/${registrationId}/${RegistrationSteps.Contact}`,
                JSON.stringify(formData),
            );
            const data = res.json() as Promise<{
                relevantIdRequestId?: string;
            }>;
            return data;
        },

        postRelevantIdData: async (
            registrationId: string,
            relevantIdRequestId: string,
            relevantIdData: Record<string, unknown>,
        ) => {
            const res = await fetcher.post(
                `/${registrationId}/save-relevantid-response/${relevantIdRequestId}`,
                JSON.stringify(relevantIdData),
            );
            return res.status;
        },

        putIdentity: async (registrationId: string, verificationData: IRegistrationIdentity) => {
            const res = await fetcher.put(
                `/${registrationId}/${RegistrationSteps.Identity}`,
                JSON.stringify(verificationData),
            );
            const data = (await res.json()) as Promise<IRegistrationIdentityResponse>;
            return data;
        },

        finishIdentity: async (registrationId: string) => {
            const res = await fetcher.put(
                `/${registrationId}/${RegistrationSteps.Identity}/finish`,
            );
            return res.status;
        },

        finishRegistration: async (registrationId: string, data: Partial<IRegistrationAddress>) => {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const res = await fetcher.post(
                `/${registrationId}/finish`,
                JSON.stringify({ ...data, timezone }),
            );
            return res.status;
        },

        // Confirmation requests

        getUserIP: async () => {
            const res = await fetch('//api.ipify.org/?format=json');
            const { ip } = (await res.json()) as { ip: string };
            return ip;
        },

        verifyPhone: async (data: IPhoneRequestData) => {
            const res = await fetcher.post(`/mobile-phone-verification`, JSON.stringify(data));
            return res.status;
        },

        emailConfirm: async (
            emailLogId: number,
            token: string,
        ): Promise<{ status: number; destinationUrl?: string }> => {
            const res = await fetcher.post('/confirm-email', JSON.stringify({ emailLogId, token }));

            const responseData = (await res.json()) as { destinationURL?: string };
            return {
                status: res.status,
                destinationUrl: responseData.destinationURL,
            };
        },

        // Yoti

        yotiCheckStatus: async (sessionId: string, topic: string) => {
            const requestBody = JSON.stringify({ sessionId, topic });
            const res = await fetcher.post('/yoti-result-webhook', requestBody);
            const data = (await res.json()) as Promise<unknown>;
            return data;
        },
    };
};

export const registerService = makeRegisterService(config.api.url.registration);
